import { useState, useRef, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';
import { useMagneticButton } from '../../../hooks/useMagneticButton';

export default function Contact() {
  const [formData, setFormData] = useState({
    nome: '',
    empresa: '',
    cargo: '',
    telefone: '',
    email: '',
    numeroFuncionarios: '',
    tipoOperacao: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  const magneticSubmitButton = useMagneticButton({ strength: 0.25, tolerance: 100 });
  
  const parallax1 = useParallax({ speed: 0.3, direction: 'up' });
  const parallax2 = useParallax({ speed: 0.4, direction: 'down' });
  const parallax3 = useParallax({ speed: 0.25, direction: 'up' });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (formRef.current) {
        const rect = formRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top
        });
      }
    };

    if (isHovering && formRef.current) {
      formRef.current.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (formRef.current) {
        formRef.current.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [isHovering]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validação
    if (!formData.nome || !formData.empresa || !formData.cargo || !formData.telefone || !formData.email || !formData.numeroFuncionarios) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) {
          formBody.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d5sdd466ql8jq9ml8qk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          empresa: '',
          cargo: '',
          telefone: '',
          email: '',
          numeroFuncionarios: '',
          tipoOperacao: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={ref}
      id="contato" 
      className={`py-24 bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      {/* Padrão de fundo animado */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 animate-pulse" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px',
          animation: 'pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite'
        }}></div>
      </div>

      {/* Elementos decorativos flutuantes com parallax */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-20 left-10 w-64 h-64 bg-teal-400/20 rounded-full blur-3xl transition-transform duration-700"
          style={{ transform: `translateY(${parallax1}px)` }}
        ></div>
        <div 
          className="absolute top-40 right-20 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl transition-transform duration-700"
          style={{ transform: `translateY(${parallax2}px)` }}
        ></div>
        <div 
          className="absolute bottom-20 left-1/3 w-72 h-72 bg-emerald-400/20 rounded-full blur-3xl transition-transform duration-700"
          style={{ transform: `translateY(${parallax3}px)` }}
        ></div>
      </div>

      {/* Partículas decorativas */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white/30 rounded-full animate-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          ></div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Lado Esquerdo - Informações */}
          <div className={`transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'
          }`}>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 hover:scale-105 transition-transform duration-300 cursor-default">
              Transforme a gestão de ponto da sua operação rural
            </h2>
            <p className="text-lg text-emerald-100 mb-8">
              Preencha o formulário e nossa equipe entrará em contato para apresentar uma solução personalizada para o seu negócio.
            </p>
            
            <div className="space-y-6 mb-10">
              {[
                { icon: 'ri-phone-line', title: 'Atendimento Rápido', desc: 'Resposta em até 24 horas úteis' },
                { icon: 'ri-shield-check-line', title: 'Sem Compromisso', desc: 'Conheça a solução sem obrigação de contratação' },
                { icon: 'ri-settings-3-line', title: 'Solução Personalizada', desc: 'Adaptamos o sistema às necessidades da sua operação' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-4 group transition-all duration-500 cursor-pointer ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
                  }`}
                  style={{ transitionDelay: `${300 + index * 100}ms` }}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-lg flex-shrink-0 group-hover:bg-white/20 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <i className={`${item.icon} text-2xl text-white group-hover:scale-110 transition-transform duration-300`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1 group-hover:text-teal-200 transition-colors">{item.title}</h3>
                    <p className="text-emerald-100">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Box de alerta reposicionado */}
            <div className={`transition-all duration-700 delay-600 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              <div className="relative bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white px-8 py-6 rounded-2xl shadow-2xl border-4 border-red-400 group hover:scale-105 transition-all duration-300 cursor-pointer overflow-hidden">
                {/* Efeito de brilho pulsante */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-red-400 to-red-500 opacity-30 group-hover:opacity-50 transition-opacity duration-500 animate-pulse"></div>
                
                {/* Ondas de alerta mais visíveis */}
                <div className="absolute -inset-1 border-4 border-red-300 rounded-2xl animate-ping opacity-60"></div>
                <div className="absolute -inset-3 border-4 border-red-400 rounded-2xl animate-ping opacity-40" style={{ animationDelay: '0.5s' }}></div>
                
                <div className="relative z-10 flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-full backdrop-blur-sm flex-shrink-0 group-hover:rotate-12 transition-transform duration-300">
                    <i className="ri-alarm-warning-line text-4xl animate-swing-question"></i>
                  </div>
                  <div>
                    <p className="font-bold text-lg mb-1">Não espere o prejuízo aumentar!</p>
                    <p className="text-sm text-white/90">Cada dia sem controle representa perdas reais</p>
                  </div>
                </div>
                
                {/* Pontos de alerta piscantes mais visíveis */}
                <div className="absolute -top-3 -right-3 w-6 h-6 bg-red-400 rounded-full animate-ping"></div>
                <div className="absolute -top-3 -right-3 w-6 h-6 bg-red-400 rounded-full shadow-lg"></div>
                <div className="absolute -bottom-3 -left-3 w-6 h-6 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute -bottom-3 -left-3 w-6 h-6 bg-red-400 rounded-full shadow-lg"></div>
              </div>
            </div>
          </div>

          {/* Lado Direito - Formulário */}
          <div className={`relative transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'
          }`}>
            {/* Brilho de fundo */}
            <div className="absolute -inset-4 bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500 rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
            
            <div 
              className="relative bg-white/95 backdrop-blur-sm rounded-2xl p-8 lg:p-10 shadow-2xl transform hover:scale-105 transition-all duration-500"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              {/* Efeito de luz seguindo o cursor */}
              {isHovering && (
                <div
                  className="absolute w-64 h-64 bg-gradient-to-r from-teal-400/20 to-cyan-400/20 rounded-full blur-3xl pointer-events-none transition-all duration-300"
                  style={{
                    left: `${mousePosition.x}px`,
                    top: `${mousePosition.y}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                ></div>
              )}

              <form 
                ref={formRef}
                id="contact-form" 
                onSubmit={handleSubmit} 
                data-readdy-form 
                className="space-y-6 relative z-10"
              >
                <div className="relative group">
                  <label htmlFor="nome" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    focusedField === 'nome' ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    Nome Completo <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('nome')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-300 group-hover:shadow-lg"
                      placeholder="Seu nome completo"
                    />
                    {focusedField === 'nome' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <i className="ri-user-line text-teal-500 animate-bounce"></i>
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative group">
                  <label htmlFor="empresa" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    focusedField === 'empresa' ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    Empresa / Fazenda <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('empresa')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-300 group-hover:shadow-lg"
                      placeholder="Nome da empresa ou fazenda"
                    />
                    {focusedField === 'empresa' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <i className="ri-building-line text-teal-500 animate-bounce"></i>
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative group">
                  <label htmlFor="cargo" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    focusedField === 'cargo' ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    Cargo <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="cargo"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('cargo')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer transition-all duration-300 hover:border-teal-300 group-hover:shadow-lg"
                  >
                    <option value="">Selecione seu cargo</option>
                    <option value="Proprietário">Proprietário</option>
                    <option value="Administrador">Administrador</option>
                    <option value="Gestor">Gestor</option>
                    <option value="Gerente de RH">Gerente de RH</option>
                    <option value="Coordenador">Coordenador</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                <div className="relative group">
                  <label htmlFor="telefone" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    focusedField === 'telefone' ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    Telefone / WhatsApp <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      value={formData.telefone}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('telefone')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-300 group-hover:shadow-lg"
                      placeholder="(00) 00000-0000"
                    />
                    {focusedField === 'telefone' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <i className="ri-whatsapp-line text-teal-500 animate-bounce"></i>
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative group">
                  <label htmlFor="email" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    focusedField === 'email' ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all duration-300 hover:border-teal-300 group-hover:shadow-lg"
                      placeholder="seu@email.com"
                    />
                    {focusedField === 'email' && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <i className="ri-mail-line text-teal-500 animate-bounce"></i>
                      </div>
                    )}
                  </div>
                </div>

                <div className="relative group">
                  <label htmlFor="numeroFuncionarios" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    focusedField === 'numeroFuncionarios' ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    Número de Funcionários <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="numeroFuncionarios"
                    name="numeroFuncionarios"
                    value={formData.numeroFuncionarios}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('numeroFuncionarios')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer transition-all duration-300 hover:border-teal-300 group-hover:shadow-lg"
                  >
                    <option value="">Selecione a quantidade</option>
                    <option value="1-10">1 a 10 funcionários</option>
                    <option value="11-50">11 a 50 funcionários</option>
                    <option value="51-100">51 a 100 funcionários</option>
                    <option value="101-500">101 a 500 funcionários</option>
                    <option value="500+">Mais de 500 funcionários</option>
                  </select>
                </div>

                <div className="relative group">
                  <label htmlFor="tipoOperacao" className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    focusedField === 'tipoOperacao' ? 'text-teal-600' : 'text-gray-900'
                  }`}>
                    Tipo de Operação Rural <span className="text-gray-500 text-xs">(opcional)</span>
                  </label>
                  <select
                    id="tipoOperacao"
                    name="tipoOperacao"
                    value={formData.tipoOperacao}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('tipoOperacao')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer transition-all duration-300 hover:border-teal-300 group-hover:shadow-lg"
                  >
                    <option value="">Selecione o tipo de operação</option>
                    <option value="Lavoura">Lavoura</option>
                    <option value="Pecuária">Pecuária</option>
                    <option value="Colheita">Colheita</option>
                    <option value="Equipes Externas">Equipes Externas</option>
                    <option value="Áreas Remotas">Áreas Remotas</option>
                    <option value="Mista">Operação Mista</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 animate-slide-in">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 animate-bounce">
                        <i className="ri-check-line text-white text-lg"></i>
                      </div>
                      <p className="text-green-800 text-sm font-medium">
                        Mensagem enviada com sucesso! Entraremos em contato em breve.
                      </p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 animate-shake">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-close-line text-white text-lg"></i>
                      </div>
                      <p className="text-red-800 text-sm font-medium">
                        Erro ao enviar mensagem. Por favor, tente novamente.
                      </p>
                    </div>
                  </div>
                )}

                <button
                  ref={magneticSubmitButton.ref as React.RefObject<HTMLButtonElement>}
                  type="submit"
                  disabled={isSubmitting}
                  className="relative w-full px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer hover:shadow-2xl overflow-hidden group"
                  style={{
                    transform: `translate(${magneticSubmitButton.position.x}px, ${magneticSubmitButton.position.y}px) scale(${magneticSubmitButton.isHovering ? 1.05 : 1})`
                  }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-teal-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
                  <span className="relative flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        Solicitar Contato
                        <i className="ri-send-plane-fill group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
                      </>
                    )}
                  </span>
                  
                  {/* Efeito de ondas no botão */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute inset-0 border-2 border-white/30 rounded-lg animate-ping"></div>
                  </div>
                  
                  {/* Efeito magnético visual */}
                  {magneticSubmitButton.isHovering && (
                    <>
                      <div className="absolute inset-0 border-2 border-white/40 rounded-lg animate-pulse"></div>
                      <div className="absolute -inset-1 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-lg blur opacity-30 animate-pulse"></div>
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-gray-600">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <p className="text-sm">
                    Conversa rápida, sem compromisso, focada na sua realidade
                  </p>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  Ao enviar este formulário, você concorda em ser contatado por nossa equipe comercial.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
